/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { useOktaAuth } from "@okta/okta-react";
import { useFormikContext } from "formik";
import { useCallback, useMemo } from "react";

import { LegalCheckBoxType } from "@/app/components/legal-check-boxes";
import { AccountDetailsInput, Tenant, useAccountQuery, useTenantsQuery } from "@/app/types/generated/graphql";

import { useOnboardingContext } from "../onboarding/context";
import { RegionSelectStepProps } from "./region-selection-step.types";

export const useRegionSelectStep = (): RegionSelectStepProps => {
  const { oktaAuth } = useOktaAuth();
  const { goNext, saveCurrentStage } = useOnboardingContext();

  const { values, handleSubmit, isValid, setFieldValue, setFieldTouched, dirty, touched } =
    useFormikContext<AccountDetailsInput>();
  const { data: tenantsData, loading: tenantsLoading } = useTenantsQuery();
  const { loading: accountLoading } = useAccountQuery();
  const isLoading = useMemo(() => tenantsLoading || accountLoading, [tenantsLoading, accountLoading]);

  const tenants = useMemo(
    () =>
      (tenantsData?.tenants.filter((tenant) => tenant.localisation?.countryCode?.toLowerCase() === "us") ||
        []) as Tenant[],
    [tenantsData?.tenants],
  );
  const renewAccessToken = useCallback(() => oktaAuth.tokenManager.renew("accessToken"), [oktaAuth]);

  const handleAcknowledgementChange = useCallback(
    async (name: string) => {
      await setFieldValue(name, !values[name as keyof AccountDetailsInput]);
      await setFieldTouched(name, true, true);
    },
    [setFieldValue, values, setFieldTouched],
  );

  const isLegalCheckBoxDisabled = useCallback(
    (checkBoxType: LegalCheckBoxType): boolean => !touched[checkBoxType] && !!values[checkBoxType],
    [touched, values],
  );

  const saveMarket = useCallback(
    async (callback) => {
      await saveCurrentStage();
      dirty && renewAccessToken?.();
      callback();
    },
    [dirty, renewAccessToken, saveCurrentStage],
  );

  return {
    goNext,
    saveMarket,
    regionSelectionValues: values,
    handleAcknowledgementChange,
    handleSubmit,
    isValid,
    setFieldValue,
    formModified: dirty,
    isLoading,
    tenants,
    isLegalCheckBoxDisabled,
  };
};
